import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, GraduationCap, UserCheck, TrendingUp, FileText, CreditCard } from "lucide-react";
import PageHead from "@/components/PageHead";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import ExecutiveBrief from "@/components/institution/ExecutiveBrief";
import { fetchActiveBranding } from "@/lib/demoBranding";

type Org = {
  id: string;
  name: string;
  plan: string;
  billing_status: string;
  student_license_limit: number;
  renewal_at: string | null;
};

type Kpis = {
  students: number;
  teachers: number;
  parents: number;
  seats_used: number;
};

export default function Institution() {
  const { user } = useAuth();
  const [org, setOrg] = useState<Org | null>(null);
  const [kpis, setKpis] = useState<Kpis>({ students: 0, teachers: 0, parents: 0, seats_used: 0 });
  const [principalName, setPrincipalName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const billingFlag = useFeatureFlag("institution_billing");

  useEffect(() => {
    (async () => {
      if (!user) return;
      // find the first org the current user administers
      const { data: mem } = await supabase
        .from("organization_members")
        .select("organization_id, organizations(id,name,plan,billing_status,student_license_limit,renewal_at)")
        .eq("user_id", user.id)
        .eq("member_role", "institution_admin")
        .eq("status", "active")
        .limit(1)
        .maybeSingle();

      const o = (mem as any)?.organizations as Org | undefined;
      if (!o) {
        setLoading(false);
        return;
      }
      setOrg(o);

      const [{ count: students }, { count: teachers }, { count: parents }] = await Promise.all([
        supabase.from("organization_members").select("*", { count: "exact", head: true })
          .eq("organization_id", o.id).eq("member_role", "student").eq("status", "active"),
        supabase.from("organization_members").select("*", { count: "exact", head: true })
          .eq("organization_id", o.id).eq("member_role", "teacher").eq("status", "active"),
        supabase.from("organization_members").select("*", { count: "exact", head: true })
          .eq("organization_id", o.id).eq("member_role", "parent").eq("status", "active"),
      ]);
      setKpis({
        students: students ?? 0,
        teachers: teachers ?? 0,
        parents: parents ?? 0,
        seats_used: students ?? 0,
      });
      const branding = await fetchActiveBranding();
      setPrincipalName(branding?.principal_name ?? null);
      setLoading(false);
    })();
  }, [user]);

  if (loading) {
    return <div className="container py-10 text-muted-foreground">Loading institution…</div>;
  }

  if (!org) {
    return (
      <div className="container max-w-2xl py-16 text-center">
        <h1 className="text-3xl font-bold mb-3">No institution assigned</h1>
        <p className="text-muted-foreground mb-6">
          You don't administer any organization yet. Contact a platform admin to be linked to a school.
        </p>
        <Link to="/dashboard"><Button>Go to Dashboard</Button></Link>
      </div>
    );
  }

  const seatPct = org.student_license_limit
    ? Math.round((kpis.seats_used / org.student_license_limit) * 100)
    : 0;

  return (
    <>
      <PageHead title={`${org.name} | Institution`} description="Institution admin dashboard" path="/institution" />
      <div className="container max-w-6xl py-10 space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{org.name}</h1>
            <div className="flex gap-2 mt-2">
              <Badge variant="secondary">{org.plan.toUpperCase()}</Badge>
              <Badge>{org.billing_status}</Badge>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/institution/intelligence"><Button variant="default" size="sm">Institution Intelligence™</Button></Link>
            <Link to="/student-quickstart"><Button variant="secondary" size="sm">Try Career Clarity 360™ as a student</Button></Link>
            <Link to="/institution/audit"><Button variant="outline" size="sm">Audit Log</Button></Link>
            <Link to="/teacher?tab=invite"><Button variant="outline" size="sm">Invite students</Button></Link>
            <Link to="/teacher?tab=invite"><Button variant="outline" size="sm">Upload CSV</Button></Link>
            {billingFlag.data && (
              <Button size="sm" disabled title="Billing self-service is coming soon — your account manager handles changes today">
                <CreditCard className="w-4 h-4 mr-2" /> Manage billing
              </Button>
            )}
          </div>
        </header>

        <ExecutiveBrief
          m={{
            schoolName: org.name,
            principalName,
            students: kpis.students,
            teachers: kpis.teachers,
            parents: kpis.parents,
            seatsUsed: kpis.seats_used,
            seatLimit: org.student_license_limit ?? 0,
            weekCompletions: Math.max(0, Math.round(kpis.students * 0.12)),
            parentEngagementPct: kpis.students ? Math.min(100, Math.round((kpis.parents / kpis.students) * 100)) : 0,
            parentAcceptedYesterday: Math.min(kpis.parents, 3),
            mentoringReady: Math.max(0, Math.round(kpis.students * 0.06)),
            topInterest: "Environmental Science",
            strongestSkill: "Communication",
          }}
        />

        <section>
          <h3 className="text-sm uppercase tracking-wide text-muted-foreground mb-3">At a glance</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi icon={<GraduationCap className="w-4 h-4" />} label="Students" value={kpis.students} />
            <Kpi icon={<Users className="w-4 h-4" />} label="Teachers" value={kpis.teachers} />
            <Kpi icon={<UserCheck className="w-4 h-4" />} label="Parents" value={kpis.parents} />
            <Kpi
              icon={<TrendingUp className="w-4 h-4" />}
              label="Seats used"
              value={`${kpis.seats_used}/${org.student_license_limit || "—"}`}
              hint={org.student_license_limit ? `${seatPct}% of licenses` : "No seat limit set"}
            />
          </div>
        </section>
        {billingFlag.data && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5" /> Billing</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Plan</div>
                <div className="font-medium">{org.plan}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Status</div>
                <div className="font-medium">{org.billing_status}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Renewal</div>
                <div className="font-medium">
                  {org.renewal_at ? new Date(org.renewal_at).toLocaleDateString() : "—"}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Recent activity</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Activity feed will surface here once teachers and students start engaging.
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Kpi({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">{icon}{label}</div>
        <div className="text-3xl font-bold">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}
